import { redirect } from 'react-router';

import { queryClient } from '@app/providers/query';

import {
  myPermissionsQueryOptions,
  PermissionAction,
  type PermissionAction as ClientPermissionAction,
  type PermissionResource as ClientPermissionResource,
} from '@entities/client/permission';
import type {
  PermissionAction as TenantPermissionAction,
  PermissionResource as TenantPermissionResource,
} from '@entities/tenant/permission';

import { resolveHomePath } from './resolve-home-path';

// Loader factory: gates a route group on `Resource:Read` (opening a page = viewing).
// Action-specific rights (Write/Delete) are checked in the UI/mutations, not in routing.
// Fetches the current user's permissions once (cached via React Query, shared across
// all gated routes) and, if the resource is not readable, redirects to the principal's
// resolved home. Never a hardcoded '/': '/' is client-gated, so redirecting a tenant
// there bounced back into this gate and froze the tab in an infinite redirect loop.
export const requirePermission =
  (
    resource: ClientPermissionResource | TenantPermissionResource,
    action: ClientPermissionAction | TenantPermissionAction = PermissionAction.Read,
  ) =>
  async () => {
    const permissions = await queryClient.ensureQueryData(myPermissionsQueryOptions());

    // App-agnostic gate: resource/action may come from either app's enum, so build the
    // `${Resource}:${Action}` key directly instead of a single app's typed hasPermission.
    if (!permissions.includes(`${resource}:${action}`))
      throw redirect(await resolveHomePath());

    return null;
  };
